import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card } from '@copilot/ui';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import * as adminApi from '../../services/adminApi';

const MAX_AGE_MS = 25000;

function time(value) {
  return value ? new Date(value).toLocaleString() : 'Not received';
}

export default function DeploymentSafetyPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('Server update');
  const [receivedAt, setReceivedAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const generation = useRef(0);
  const mutating = useRef(false);
  const mounted = useRef(false);

  const accept = useCallback((result) => {
    if (!result || typeof result.draining !== 'boolean' || !Array.isArray(result.meetings)
      || !Number.isInteger(result.blocking_meetings) || result.blocking_meetings < 0
      || typeof result.safe_to_deploy !== 'boolean' || !Number.isFinite(Date.parse(result.checked_at))) {
      throw new Error('Invalid meeting status response. Do not update the server.');
    }
    setData(result);
    setReceivedAt(Date.now());
    setNow(Date.now());
    setError('');
  }, []);

  const refresh = useCallback(async ({ isStale = () => !mounted.current } = {}) => {
    if (mutating.current) return;
    const request = ++generation.current;
    try {
      const result = await adminApi.getDeploymentStatus();
      if (!isStale() && request === generation.current) accept(result);
    } catch (err) {
      if (!isStale() && request === generation.current) {
        setError(err.message || 'Meeting status could not be checked.');
      }
    }
  }, [accept]);

  useEffect(() => {
    mounted.current = true;
    void refresh();
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      mounted.current = false;
      generation.current += 1;
      clearInterval(timer);
    };
  }, [refresh]);
  useAutoRefresh(refresh);

  async function changeMode() {
    if (mutating.current) return;
    mutating.current = true;
    setBusy(true);
    const request = ++generation.current;
    // Immediately withdraw an earlier ready signal during a mutation.
    setReceivedAt(0);
    try {
      const result = await adminApi.updateDeploymentControl({ draining: !data.draining, reason: reason.trim() });
      if (mounted.current && request === generation.current) accept(result);
    } catch (err) {
      if (mounted.current) setError(`Update mode could not be confirmed: ${err.message || 'check server status before continuing.'}`);
    } finally {
      mutating.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  const fresh = receivedAt > 0 && now - receivedAt <= MAX_AGE_MS;
  const ready = !error && !busy && fresh && data?.draining === true
    && data?.blocking_meetings === 0 && data?.safe_to_deploy === true;
  let headline = 'Checking meeting status — do not update yet';
  if (error || (data && !fresh)) headline = 'Meeting status is unverified — do not update';
  else if (ready) headline = 'Ready for a guarded server update';
  else if (data?.blocking_meetings > 0) headline = 'Update blocked — meetings are still in progress';
  else if (data) headline = 'Pause new meetings before updating';

  return (
    <Card className="deployment-safety-panel">
      <h2>Server update safety</h2>
      <p role="status" className={ready ? 'deployment-safety-ready' : 'deployment-safety-warning'}>{headline}</p>
      {error && <p role="alert">{error}</p>}
      {data && (
        <>
          <p>{data.blocking_meetings} ongoing meeting(s) · {data.active_meetings} active · {data.paused_meetings} paused · {data.bot_jobs} bot job(s)</p>
          <p>{data.draining
            ? 'New meetings are paused. Existing meetings can finish, reconnect and resume.'
            : 'New meetings are allowed. An empty list alone does not make an update safe.'}</p>
          <p>Last server check: {time(data.checked_at)}. Refreshes every 10 seconds while this page is visible.</p>
          {data.updated_by && <p>Last mode change: {time(data.updated_at)}. Reason: {data.reason}</p>}
          <div className="deployment-safety-table">
            <table>
              <caption>Meetings that block a server update</caption>
              <thead><tr><th scope="col">User</th><th scope="col">Meeting</th><th scope="col">State / client</th><th scope="col">Started</th><th scope="col">Last heartbeat</th></tr></thead>
              <tbody>{data.meetings.map((meeting) => (
                <tr key={meeting.id}>
                  <td><div>{meeting.user_name || 'Unknown user'}</div><div>{meeting.user_email}</div></td>
                  <td><a href={`/admin/meetings/${meeting.id}`}>{meeting.title}</a></td>
                  <td>{meeting.status} · {meeting.client_type || meeting.attendance_mode}<br />{meeting.bot_states.join(', ')}</td>
                  <td>{time(meeting.started_at)}</td>
                  <td>{meeting.attendance_mode === 'bot' ? 'Tracked through bot state' : <>{time(meeting.last_activity_at)}{meeting.heartbeat_stale && <strong> — stale or missing; still blocks updates</strong>}</>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          {data.meetings.length === 0 && <p>No blocking meetings found at the last check.</p>}
          {data.truncated && <p>Showing the first {data.meetings.length} meetings. All {data.blocking_meetings} meetings count toward the update check.</p>}
          <div className="deployment-safety-actions">
            <label>Reason for mode change <input value={reason} maxLength={300} onChange={(event) => setReason(event.target.value)} disabled={busy} /></label>
            <Button onClick={changeMode} disabled={busy || !fresh || !!error || reason.trim().length < 3}>
              {busy ? 'Saving update mode...' : data.draining ? 'Allow new meetings' : 'Pause new meetings'}
            </Button>
          </div>
          <p>Use <code>make safe-update</code> after the guarded check passes. Keep new meetings paused until server verification is complete.</p>
        </>
      )}
      <Button variant="secondary" onClick={() => void refresh()} disabled={busy}>Refresh meeting status</Button>
    </Card>
  );
}
